<template>
  <div class="card custom-shadow rounded-3 bg-white border mb-4">
    <div class="card-body p-4">
      <!-- Table Title -->
      <div class="row">
        <div class="col-12">
          <h4 class="mb-0 fw-semibold" v-html="t('fr.liquidity.planning_title', 'Pianificazione delle rate')"></h4>
           <p class="mb-3" v-html="t('fr.liquidity.planning_description', 'Ogni mese accantoni la cifra giusta. Così quando arriva la scadenza sei già pronto, senza sorprese.')"></p>
        </div>
      </div>

      <!-- Debug Info 
      <div class="alert alert-info">
          DEBUG: <br>
          Total Events: {{ sortedEvents.length }} <br>
          Total Pages: {{ totalPages }} <br>
          Items Per Page: {{ itemsPerPage }} <br>
          Current Page: {{ currentPage }} <br>
          Paginated Events Count: {{ paginatedEvents.length }}
      </div>-->

      <!-- Table Title -->
      <div class="default-table-area style-two campaigns-table">
        <div class="table-responsive">
          <table class="table align-middle border-0">
            <thead>
              <tr>
                <th scope="col" class="ps-0">Evento</th>
                <th scope="col">Categoria</th>
                <th scope="col">Tipo</th>
                <th scope="col" class="text-end">Importo</th>
                <th scope="col" class="text-end">Saldo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(event, index) in paginatedEvents" :key="index">
                <td class="ps-0">
                  <div :class="['border-start', 'border-2', 'ps-3', 'py-0', getRowBorderClass(event.isIncome)]">
                    <h6 class="fs-14 fw-semibold mb-0" v-html="event.description"></h6>
                    <span class="fs-12">{{ formatDate(event.date) }}</span>
                  </div>
                </td>
                <td>
                  <span class="d-inline-block p-2 rounded-2 fw-semibold" :style="getCategoryLabelStyle(event.category)" v-html="getCategoryLabel(event.category)">
                  </span>
                </td>
                <td>
                  <span class="d-inline-block p-2 rounded-2 fw-semibold" :style="getTypeStyle(event.status)" v-html="event.status">
                  </span>
                </td>
                <td class="text-end">
                  <span :class="getAmountClass(event.isIncome)">
                    {{ formatCurrency(event.amount) }}
                  </span>
                </td>
                <td class="text-end fw-medium">{{ formatCurrency(event.balanceAfter) }}</td>
              </tr>
              <tr v-if="paginatedEvents.length === 0">
                <td colspan="5" class="text-center py-4">Nessun evento da visualizzare.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Items per page and Pagination Controls -->
        <div class="d-flex justify-content-between align-items-center mt-3">
          <!-- Items per page selector -->
          <div class="d-flex align-items-center">
            <label class="me-2 fw-medium fs-14">Righe per pagina:</label>
            <select
              v-model="itemsPerPage"
              @change="onItemsPerPageChange"
              class="form-select form-select-sm"
              style="width: auto;"
            >
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="30">30</option>
            </select>
          </div>

          <!-- Pagination Controls -->
          <nav v-if="totalPages > 1" aria-label="Page navigation">
            <ul class="pagination justify-content-center mb-0">
              <li class="page-item" :class="{ 'disabled': currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="prevPage"><i class="ri-arrow-left-line"></i></a>
              </li>
              <li class="page-item" v-for="page in totalPages" :key="page" :class="{ 'active': page === currentPage }">
                <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
              </li>
              <li class="page-item" :class="{ 'disabled': currentPage === totalPages }">
                <a class="page-link" href="#" @click.prevent="nextPage"><i class="ri-arrow-right-line"></i></a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, computed, ref } from 'vue';
import { t } from '@/utils/i18nHelper';

const props = defineProps({
  schedule: {
    type: Object,
    required: true,
  },
});

const sortedEvents = computed(() => {
  if (!props.schedule || !props.schedule.events) return [];
  // Sort events by date
  return [...props.schedule.events].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
});

// --- Pagination Logic ---
const currentPage = ref(1);
const itemsPerPage = ref(5); // Show 5 items per page

const totalPages = computed(() => {
  return Math.ceil(sortedEvents.value.length / itemsPerPage.value);
});

const paginatedEvents = computed(() => {
  if (sortedEvents.value.length === 0) return [];
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return sortedEvents.value.slice(start, end);
});

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const goToPage = (page: number) => {
  currentPage.value = page;
};

const onItemsPerPageChange = () => {
  currentPage.value = 1; // Reset to first page when changing items per page
};
// --- End Pagination Logic ---

const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
  return new Date(dateString).toLocaleDateString('it-IT', options);
};

// Uso diretto della funzione t() nel template

const formatCurrency = (value: number) => {
  if (typeof value !== 'number') return 'N/A';
  return value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
};

const getAmountClass = (isIncome: boolean) => {
  return isIncome ? 'border-success' : 'border-danger';
};

const getRowBorderClass = (isIncome: boolean) => {
  return isIncome ? 'border-success' : 'border-danger';
};

const hexToRgba = (hex: string, alpha: number) => {
  if (!/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        return `rgba(108, 117, 125, ${alpha})`;
    }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// --- Styling for Category & Type Labels ---

const categoryColors: { [key: string]: string } = {
  IRES: '#dc3545',        // Rosso - coerente con TaxBreakdownChart
  INPS: '#fd7e14',        // Arancione - coerente con TaxBreakdownChart
  INPS_FISSI: '#ffc107',  // Giallo - coerente con TaxBreakdownChart
  // Casse Professionali
  SOGGETTIVO: '#fd7e14',  // Arancione (come INPS Variable)
  INTEGRATIVO: '#ffc107', // Giallo (come INPS Fixed)
  MATERNITA: '#9c27b0',   // Viola (specifico per maternità)
  // Altri
  ACCUMULO: '#6a7cf9',
  DEFAULT: '#6c757d'
};

const getCategoryLabel = (category: string) => {
  const keyMap = {
    'IRES': 'fr.deadlines.category_taxes',
    'INPS': 'fr.deadlines.category_inps_variable',
    'INPS_FISSI': 'fr.deadlines.category_inps_fixed',
    // Casse Professionali
    'SOGGETTIVO': 'fr.deadlines.category_contrib_soggettivo',
    'INTEGRATIVO': 'fr.deadlines.category_contrib_integrativo',
    'MATERNITA': 'fr.deadlines.category_contrib_maternita',
    // Altri
    'ACCUMULO': 'fr.liquidity.avg_monthly_rate'
  };

  const defaultLabels = {
    'IRES': 'Imposte',
    'INPS': 'INPS Variabili',
    'INPS_FISSI': 'INPS Fissi',
    // Casse Professionali
    'SOGGETTIVO': 'Contrib. Soggettivo',
    'INTEGRATIVO': 'Contrib. Integrativo',
    'MATERNITA': 'Contrib. Maternità',
    // Altri
    'ACCUMULO': 'Rata'
  };

  const key = keyMap[category as keyof typeof keyMap];
  const defaultLabel = defaultLabels[category as keyof typeof defaultLabels] || category;

  return key ? t(key, defaultLabel) : defaultLabel;
};

const getCategoryLabelStyle = (category: string) => {
  const color = categoryColors[category] || categoryColors.DEFAULT;
  return {
    backgroundColor: hexToRgba(color, 0.1),
    color: color,
    fontSize: '12px',
  };
};

const getTypeStyle = (status: string) => {
  let color = categoryColors.DEFAULT;
  if (status === 'Versamento') {
    color = categoryColors.ACCUMULO; // Use Rata/Accumulo color
  } else if (status && status.startsWith('Pagamento')) {
    color = '#fd5812'; // Use Danger color from theme
  }
  return {
    backgroundColor: hexToRgba(color, 0.1),
    color: color,
    fontSize: '12px',
  };
};

</script>

<style scoped>
.border-success {
  border-color: #6a7cf9 !important; /* Use Rata color for income border */
}
.border-danger {
  border-color: #fd5812 !important;
}
.text-success {
  color: #37d80a !important;
}
.text-danger {
  color: #fd5812 !important;
}
.text-primary {
  color: #304BF7 !important;
}

/* Replicating .default-table-area.style-two.campaigns-table styles */
:deep(.default-table-area.style-two.campaigns-table .table-responsive) {
  border-radius: 0 !important;
}

:deep(.default-table-area.style-two.campaigns-table table thead tr th) {
  background-color: transparent;
  font-size: 14px;
  padding: 15px 8px;
  border: none !important;
}

:deep(.default-table-area.style-two.campaigns-table table thead tr th:first-child) {
  padding-left: 0 !important;
}

:deep(.default-table-area.style-two.campaigns-table table thead tr th:last-child) {
  padding-right: 0 !important;
}

:deep(.default-table-area.style-two.campaigns-table table tbody tr td) {
  padding-top: 10px;
  padding-bottom: 10px;
  border-left: none;
  border-right: none;
  border-top: 1px solid #eceef2; /* From theme $border-color */
}

:deep(.default-table-area.style-two.campaigns-table table tbody tr:first-child td) {
  border-top: none;
}


:deep(.default-table-area.style-two.campaigns-table table tbody tr td:first-child) {
  padding-left: 0 !important;
}

:deep(.default-table-area.style-two.campaigns-table table tbody tr td:last-child) {
  padding-right: 0 !important;
}
</style>